import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiKeyService } from '../services/api-key-service';
import httpClient from '../services/http-client';
import { useAuth } from './use-auth';
import { 
  ApiKeyListResponse,
  ApiKeyWithPlainText,
  CreateApiKeyRequest
} from '../types';

// Create an API key service instance using the default HTTP client
const apiKeyService = new ApiKeyService(httpClient);

// Query keys
export const apiKeyQueryKeys = {
  all: ['apiKeys'] as const,
  lists: () => [...apiKeyQueryKeys.all, 'list'] as const,
  list: (divisionId: string) => [...apiKeyQueryKeys.lists(), divisionId] as const,
};

/**
 * Hook to get all API keys for a specific division
 * Only makes API calls if user has DivisionAdmin role
 */
export function useDivisionApiKeys(divisionId: string) {
  const { isAdmin: hasAdminRole } = useAuth();

  return useQuery({
    queryKey: apiKeyQueryKeys.list(divisionId),
    queryFn: () => apiKeyService.getDivisionApiKeys(divisionId),
    enabled: hasAdminRole && !!divisionId, // Only enable if user has admin role and divisionId is provided
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to create a new API key
 */
export function useCreateApiKey(divisionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateApiKeyRequest) => 
      apiKeyService.createApiKey(divisionId, request),
    onSuccess: () => {
      // Invalidate and refetch API keys list
      queryClient.invalidateQueries({ queryKey: apiKeyQueryKeys.list(divisionId) });
    },
  });
}

/**
 * Hook to delete an API key
 */
export function useDeleteApiKey(divisionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (apiKeyId: string) => 
      apiKeyService.deleteApiKey(divisionId, apiKeyId),
    onSuccess: () => {
      // Invalidate and refetch API keys list
      queryClient.invalidateQueries({ queryKey: apiKeyQueryKeys.list(divisionId) });
    },
  });
}
